<!-- 数据导入弹窗 -->
<template>
  <ele-modal
    form
    :width="width"
    v-bind="attrs"
    :model-value="modelValue"
    @update:modelValue="updateModelValue"
    :title="titleLang"
    position="center"
    style="max-width: 95%"
  >
    <el-form ref="formRef" :model="form" label-width="92px" @submit.prevent="">
      <el-upload
        ref="uploadRef"
        style="width: 100%"
        class="upload-demo"
        drag
        :auto-upload="false"
        :on-change="fileChange"
        :on-exceed="handleExceed"
        :limit="1"
        :accept="accept"
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text" v-if="t('lang') === 'zh_TW'">
          將檔案拖到此处，或<em>點擊上傳</em>
        </div>
        <div class="el-upload__text" v-else-if="t('lang') === 'en'">
          Drag your file here, or <em>click to upload</em>
        </div>
        <div class="el-upload__text" v-else>
          将文件拖到此处，或<em>点击上传</em>
        </div>
        <template #tip>
          <div class="el-upload__tip" v-if="t('lang') === 'zh_TW'"
            >只能上傳 <strong>{{ getAccepts() }}</strong> 格式的檔案</div
          >
          <div class="el-upload__tip" v-else-if="t('lang') === 'en'"
            >Only <strong>{{ getAccepts() }}</strong> format files can be
            uploaded</div
          >
          <div class="el-upload__tip" v-else
            >只能上传 <strong>{{ getAccepts() }}</strong> 格式的文件</div
          >
        </template>
      </el-upload>
      <el-divider style="margin: 8px 0 0px 0; opacity: 0.6" />
      <el-link
        v-if="templateLink"
        type="primary"
        underline
        :href="templateLink"
        :download="templateFileName"
      >
        {{ downloadLinkTitle }}
      </el-link>
    </el-form>
    <template #footer>
      <el-button @click="updateModelValue(false)">
        {{ cancelTitleLang }}
      </el-button>
      <el-button type="primary" :loading="loading" @click="executeImport">
        {{ confirmlTitleLang }}
      </el-button>
    </template>
  </ele-modal>
</template>

<script lang="ts" setup>
  import { ref, watch, computed, useAttrs } from 'vue';
  import type { FormInstance, UploadFile, UploadRawFile } from 'element-plus';
  import { useI18n } from 'vue-i18n';

  import { useFormData } from '@/utils/use-form-data';
  import { EleMessage } from 'ele-admin-plus/es';
  import LzdUtil, { dataFillCurrInfo, JSONUtil } from '@/utils/lzd-util';
  import type { R } from '@/api';
  import { RParam } from '@/api';
  import request from '@/utils/request';
  import { getCacheLang } from '@/i18n/use-locale';

  const { t } = useI18n();
  const attrs = useAttrs();

  const emit = defineEmits<{
    (e: 'update:modelValue', modelValue: boolean): void;
    /** 导入执行回调 */
    (e: 'done', data: R<any>): void;
  }>();

  /** 更新modelValue */
  const updateModelValue = (value: boolean) => {
    emit('update:modelValue', value);
  };

  const props = withDefaults(
    defineProps<{
      /** 弹窗是否打开 */
      modelValue: boolean;
      /** 弹窗宽度 */
      width?: number;
      /** 标题（简体中文） */
      title?: string;
      /** 标题（繁体中文） */
      titleTw?: string;
      /** 标题（英文） */
      titleEn?: string;
      /** 取消按钮（简体中文） */
      cancelTitle?: string;
      /** 取消按钮（繁体中文） */
      cancelTitleTw?: string;
      /** 取消按钮（英文） */
      cancelTitleEn?: string;
      /** 确认按钮（简体中文） */
      confirmlTitle?: string;
      /** 确认按钮（繁体中文） */
      confirmlTitleTw?: string;
      /** 确认按钮（英文） */
      confirmlTitleEn?: string;
      /** 格式，多个逗号分割，如 .xls, .xlsx */
      accept?: string;
      /** 模板下载链接 */
      templateLink?: string;
      /** 模板名称 */
      templateFileName?: string;
      /** 导入地址 */
      url: string;
      /** 其他参数 */
      where?: Record<any, any>;
    }>(),
    {
      width: 740,
      title: '导入数据',
      titleTw: '匯入資料',
      titleEn: 'Import data',
      cancelTitle: '取消',
      cancelTitleTw: '取消',
      cancelTitleEn: 'Cancel',
      confirmlTitle: '执行导入',
      confirmlTitleTw: '執行匯入',
      confirmlTitleEn: 'Execute Import',
      closeOnClickModal: false,
      accept: '.xls, .xlsx'
    }
  );

  /** 提交状态 */
  const loading = ref(false);

  /** 表单实例 */
  const formRef = ref<FormInstance | null>(null);
  const uploadRef = ref();
  const files = ref<File | null>(null);
  let fileIdx = 1;

  /** 表单数据 */
  const [form, resetFields] = useFormData<any>({
    file: files.value as File
  });

  const getAccepts = () => {
    return props.accept
      .split(',')
      .map((v) => v.trim())
      .join('、');
  };

  /** 弹窗标题 文本 */
  const titleLang = computed<string>(() => {
    const cacheLang = t('lang');
    if (cacheLang === 'zh_TW') {
      return props.titleTw ?? props.title;
    }
    if (cacheLang === 'en') {
      return props.titleEn ?? props.title;
    }
    return props.title;
  });

  /** 下载模板 文本 */
  const downloadLinkTitle = computed<string>(() => {
    const cacheLang = t('lang');
    if (cacheLang === 'zh_TW') {
      return '下載模板';
    }
    if (cacheLang === 'en') {
      return 'Download template';
    }
    return '下载模板';
  });

  /** 取消 文本 */
  const cancelTitleLang = computed<string>(() => {
    const cacheLang = t('lang');
    if (cacheLang === 'zh_TW') {
      return props.cancelTitleTw ?? props.cancelTitle;
    }
    if (cacheLang === 'en') {
      return props.cancelTitleEn ?? props.cancelTitle;
    }
    return props.cancelTitle;
  });

  /** 执行导入文本 文本 */
  const confirmlTitleLang = computed<string>(() => {
    const cacheLang = t('lang');
    if (cacheLang === 'zh_TW') {
      return props.confirmlTitleTw ?? props.confirmlTitle;
    }
    if (cacheLang === 'en') {
      return props.confirmlTitleEn ?? props.confirmlTitle;
    }
    return props.confirmlTitle;
  });

  /** 处理文件变化 */
  const fileChange = (file: UploadFile) => {
    const cacheLang = getCacheLang();
    if (props.accept) {
      if (
        !props.accept
          .split(',')
          .map((v) => v.trim())
          .includes(file.name.substring(file.name.lastIndexOf('.')))
      ) {
        form.file = null;
        if ('zh_TW' === cacheLang) {
          EleMessage.error(`僅支援 ${props.accept} 格式匯入`);
        } else if ('en' === cacheLang) {
          EleMessage.error(`Only supports ${props.accept} format import`);
        } else {
          EleMessage.error(`仅支持 ${props.accept} 格式导入`);
        }
        return;
      }
    }

    form.file = file.raw;
  };

  const handleExceed = (files: File[]) => {
    const f = files[0] as UploadRawFile;
    f.uid = Date.now() + fileIdx++;
    form.file = f;
    uploadRef.value?.clearFiles?.();
    uploadRef.value?.handleStart?.(f);
  };

  /** 执行导入 */
  const executeImport = async () => {
    formRef.value?.validate?.((valid) => {
      if (!valid) {
        return;
      }
      if (!form.file) {
        const cacheLang = getCacheLang();
        if ('zh_TW' === cacheLang) {
          EleMessage.error(`未選擇檔案`);
        } else if ('en' === cacheLang) {
          EleMessage.error(`No file selected`);
        } else {
          EleMessage.error(`没有选择文件`);
        }
        return;
      }

      const formData = new FormData();
      formData.set('file', form.file);
      formData.set(
        'param',
        JSONUtil.toStr(
          dataFillCurrInfo({
            where: RParam.buildWhere(props.where)
          })
        )
      );
      loading.value = true;
      request
        .post<R<any>>(props.url, formData)
        .then((res) => {
          updateModelValue(false);
          emit('done', res.data);
          loading.value = false;
        })
        .catch((err) => {
          LzdUtil.tipsError(err);
          loading.value = false;
        });
    });
  };

  watch(
    () => props.modelValue,
    (modelValue) => {
      if (modelValue) {
        //打开
        form.file = null;
      } else {
        resetFields();
        uploadRef.value.clearFiles();
        form.file = null;
      }
    }
  );
</script>

<script lang="ts">
  export default {
    name: 'LzdDataTableImport'
  };
</script>
<style lang="scss" scoped>
  .el-upload__tip {
    color: red;
  }
</style>
